import React, { useState, ReactNode, useEffect, useRef } from "react";
import { Check, ChevronDown, ChevronUp, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SectionContainerProps {
  title?: string | ReactNode;
  children: ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  isTitleEditable?: boolean;
  onSaveTitle?: (newTitle: string) => void;
  isRequired?: boolean;
  scrollOnExpand?: boolean;
  headerRight?: ReactNode;
}

const SectionContainer = React.forwardRef<
  HTMLDivElement,
  SectionContainerProps
>(
  (
    {
      title,
      children,
      isExpanded,
      onToggle,
      isTitleEditable = false,
      onSaveTitle,
      isRequired = false,
      scrollOnExpand = true,
      headerRight
    },
    ref
  ) => {
    const [internalExpanded, setInternalExpanded] = useState(true);
    const expandedState = isExpanded ?? internalExpanded;
    const [isEditing, setIsEditing] = useState(false);
    const [newTitle, setNewTitle] = useState(
      typeof title === "string" ? title : ""
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const [prevExpandedState, setPrevExpandedState] = useState(expandedState);

    const handleToggle = () => {
      const newState = !expandedState;
      setInternalExpanded(newState);
      onToggle?.();
    };

    // Auto-scroll to top when section is expanded (gated by scrollOnExpand)
    useEffect(() => {
      if (
        scrollOnExpand &&
        expandedState &&
        !prevExpandedState &&
        containerRef.current
      ) {
        // Small delay to ensure the content is rendered
        setTimeout(() => {
          containerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }, 100);
      }
      setPrevExpandedState(expandedState);
    }, [expandedState, prevExpandedState, scrollOnExpand]);

    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsEditing(true);
    };

    const handleCancel = () => {
      setNewTitle(typeof title === "string" ? title : "");
      setIsEditing(false);
    };

    const handleApply = () => {
      onSaveTitle?.(newTitle!);
      setIsEditing(false);
    };

    const ChevronIcon = expandedState ? ChevronUp : ChevronDown;

    return (
      <div ref={ref} className="w-full">
        <div
          ref={containerRef}
          className="w-full rounded-xl bg-elevated ring-1 ring-line-2"
        >
          <div
            className="flex w-full cursor-pointer flex-row items-center gap-2 rounded-xl px-4 py-3 text-left transition-colors duration-100 hover:bg-fill-1"
            onClick={() => {
              if (!isEditing) {
                handleToggle();
              }
            }}
          >
            <ChevronIcon className="size-4 shrink-0 text-fg-3" />
            {isEditing && typeof title === "string" ? (
              <div
                className="flex flex-1 items-center gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="flex-1"
                  onClick={(e) => e.stopPropagation()}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleCancel}
                >
                  <X />
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleApply}
                >
                  <Check />
                  Apply
                </Button>
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {typeof title === "string" ? (
                  <span className="truncate text-base font-medium text-fg">
                    {title}{" "}
                    {isRequired && <span className="text-danger">*</span>}
                  </span>
                ) : (
                  title
                )}
                {isTitleEditable && typeof title === "string" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Edit title"
                    onClick={handleEdit}
                  >
                    <Pencil />
                  </Button>
                )}
              </div>
            )}
            {headerRight && !isEditing && (
              <div onClick={(e) => e.stopPropagation()}>{headerRight}</div>
            )}
          </div>
          <div
            className={cn(
              "w-full min-w-0 transition-all duration-200 ease-in-out",
              expandedState
                ? "max-h-none px-4 pt-1 pb-4 opacity-100"
                : "max-h-0 overflow-hidden p-0 opacity-0"
            )}
          >
            <div className="w-full min-w-0">{children}</div>
          </div>
        </div>
      </div>
    );
  }
);

export default SectionContainer;
SectionContainer.displayName = "SectionContainer";
